import { useState, useEffect, Fragment } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { RootState } from '../ApplicationState';
import SpinnerOverlay from '../SpinnerOverlay';
import CheckInTool from '../components/CheckInTool';
import Button from '../components/Button';
import CheckInToolMobile from '../components/CheckInToolMobile';
import StudentHeroSegment from '../components/StudentHeroSegment';
import ReviewCheckIn from './ReviewCheckIn';
import {
  listStudentAssignments,
  recordMiddleSchoolJournalEntry,
  retrieveAssignment,
  submitAssignment,
} from '../BackendInterface';
import { getMiddleSchoolJournals } from '../BackendInterface';
import { setLocalStorage, getLocalStorage, removeLocalStorage } from '../aaa';
import { isCheckInModule } from '../GlobalFunctions';
import { selectClassroom } from '../Classrooms';
import { Assignment } from '../../peekapak-types/LessonPlanTypes';
import { State, play, setMedia } from '../components/AudioPlayer/AudioPlayer';
import { Icon } from '@iconify/react';
import toDoHeader from '../images/ToDo_Page.png';
import toDoHeaderHS from '../images/ToDo_Page_HS.png';
import styles from './CheckIn.module.scss';

interface LocationState extends Location {
  status: string;
}

// --- ADD THESE RATE LIMITING HELPERS ---

const MAX_SUBMISSIONS_PER_HOUR = 3;
const CHECKIN_RATE_KEY = 'checkinSubmissionTimestamps';

const getSubmissionTimestamps = (): number[] => {
  try {
    const raw = getLocalStorage(CHECKIN_RATE_KEY);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch {
    return [];
  }
};

const saveSubmissionTimestamps = (timestamps: number[]) => {
  setLocalStorage(CHECKIN_RATE_KEY, JSON.stringify(timestamps));
};

const canSubmitEmotion = (): boolean => {
  const now = Date.now();
  const oneHourAgo = now - 60 * 60 * 1000;

  const recent = getSubmissionTimestamps().filter((ts) => ts > oneHourAgo);
  saveSubmissionTimestamps(recent); // prune old

  return recent.length < MAX_SUBMISSIONS_PER_HOUR;
};

const recordEmotionSubmission = () => {
  const now = Date.now();
  const timestamps = getSubmissionTimestamps();
  timestamps.push(now);
  saveSubmissionTimestamps(timestamps);
};

// --------------------------------------

const CheckIn = () => {
  const [isJournalEntriesLoaded, setIsJournalEntriesLoaded] = useState<boolean>(false);
  const [isCheckInAssignmentLoaded, setIsCheckInAssignmentLoaded] = useState<boolean>(false);
  const [isAssignmentSubmissionLoaded, setIsAssignmentSubmissionLoaded] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isShowSpinner, setIsShowSpinner] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  // --- NEW STATE: rate limit error ---
  const [isRateLimited, setIsRateLimited] = useState<boolean>(false);

  // isCheckingIn - show the check in tool
  const [isCheckingIn, setIsCheckingIn] = useState<boolean>(false);
  // isReviewing - show the response for the daily check in OR student's submission for the check in assignment
  const [isReviewing, setIsReviewing] = useState<boolean>(false);
  // isViewingSubmission - show student's submission for the check in assignment
  const [isViewingSubmission, setIsViewingSubmission] = useState<boolean>(false);
  const [toDoHeaderImage, setToDoHeaderImage] = useState<string>('');
  const [selectedEmotion, setSelectedEmotion] = useState<string>('');
  const [journalEntries, setJournalEntries] = useState<string[]>([]);
  const [checkInAssignment, setCheckInAssignment] = useState<Assignment>();
  const [assignmentSubmission, setAssignmentSubmission] = useState<string[]>();
  const [timeSubmitted, setTimeSubmitted] = useState<number | undefined>();

  const { userId, media, isPlayerLoading } = useSelector((state: RootState) => ({
    userId: state.user.userProfile?.userId ? state.user.userProfile.userId : '',
    media: state.audioPlayer.media,
    isPlayerLoading: state.audioPlayer.state === State.loading,
  }));
  const classroom = useSelector((state: RootState) => {
    return selectClassroom(state);
  });
  const userProfile = useSelector((state: RootState) => {
    return state.user.userProfile;
  });

  const language = classroom?.language || 'en';
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const isSmall = useMediaQuery({ query: '(max-width: 820px)' });

  const getToDoHeaderImage = async () => {
    try {
      if (userProfile && userProfile.type === 'Teacher' && classroom) {
        classroom.grade > 8 ? setToDoHeaderImage(toDoHeaderHS) : setToDoHeaderImage(toDoHeader);
      } else if (userProfile && userProfile.type === 'Student') {
        userProfile.grade > 8 ? setToDoHeaderImage(toDoHeaderHS) : setToDoHeaderImage(toDoHeader);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const isCurrentStoryMediaLoaded = () => media?.title === 'Check-In';
  const setCheckInMedia = () => {
    dispatch(
      setMedia({
        sources: {
          mp3: 'https://peekaplatform.s3.amazonaws.com/assets/premium/storyAudioMiddleSchool/Check-In-Wheel.wav',
        },
        title: 'Check-In',
      }),
    );
  };
  const handlePlayAudio = () => {
    if (!isCurrentStoryMediaLoaded()) {
      setCheckInMedia();
    }
    dispatch(play());
  };

  const storeIsCompletedOrOptedOutFlag = () => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const minutesPassed = hours * 60 + minutes;
    const minutesPerDay = 24 * 60;
    const minutesRemaining = minutesPerDay - minutesPassed;
    setLocalStorage('isCheckInCompleteOrOptedOut', 'true', minutesRemaining);
  };

  // --- UPDATE handleEmotionClick to clear rate-limit error ---
  const handleEmotionClick = (emotion: string) => {
    setSelectedEmotion(emotion);
    setIsError(false);
    setIsRateLimited(false);
  };

  const handleGoHome = () => {
    history.push('/studentportal/toDos');
  };

  const handleResetSubmission = () => {
    setIsCheckingIn(true);
    setIsReviewing(false);
    setSelectedEmotion('');
    removeLocalStorage('isCheckInCompleteOrOptedOut');
  };

  const handleSkipToday = () => {
    storeIsCompletedOrOptedOutFlag();
    history.push('/studentportal/toDos');
  };

  const retrieveAssignmentSubmission = async (userId: string, createdAt: number) => {
    try {
      const toDoPayload = await retrieveAssignment(userId, createdAt);
      if (toDoPayload && toDoPayload[0] && toDoPayload[0].work && toDoPayload[0].work.content) {
        const content = toDoPayload[0].work.content;
        setAssignmentSubmission(content);
        setTimeSubmitted(toDoPayload[0].modifiedAt);
      }
    } catch {
      console.error('could not retrieve check in assignment');
    } finally {
      setIsAssignmentSubmissionLoaded(true);
    }
  };

  const retrieveJournalEntries = async () => {
    try {
      const journalEntries = await getMiddleSchoolJournals(userId);
      const now = new Date();
      const today = new Date(now.setHours(0, 0, 0, 0));
      const emotions = journalEntries.reduce((acc, curr) => {
        if (new Date(curr.createdAt) > today) acc.push(curr.feeling);
        return acc;
      }, [] as string[]);
      setJournalEntries(emotions);
    } finally {
      setIsJournalEntriesLoaded(true);
    }
  };

  // --- UPDATE submitJournalEntry to check rate limit ---
  const submitJournalEntry = async () => {
    setIsShowSpinner(true);
    try {
      if (selectedEmotion === '') {
        setIsError(true);
        setIsShowSpinner(false);
        return;
      }

      if (!canSubmitEmotion()) {
        setIsRateLimited(true);
        setIsShowSpinner(false);
        return;
      }

      recordMiddleSchoolJournalEntry(userId, selectedEmotion, '', false);
      recordEmotionSubmission();

      storeIsCompletedOrOptedOutFlag();
      journalEntries.push(selectedEmotion);

      if (checkInAssignment && checkInAssignment.status !== 'submitted') {
        const content = selectedEmotion;
        await submitAssignment(userId, checkInAssignment.createdAt, [content]);
        setAssignmentSubmission([content]);
        setIsViewingSubmission(true);
      } else {
        setIsViewingSubmission(false);
      }
      setIsCheckingIn(false);
      setIsReviewing(true);
    } catch {
      console.error('could not submit emotion to database');
    } finally {
      setIsShowSpinner(false);
    }
  };

  const getCheckInAssignment = async () => {
    setIsShowSpinner(true);
    try {
      if (userProfile) {
        const studentToDos = await listStudentAssignments(userId, language, userProfile.grade);
        const toDo = studentToDos.find((toDo) => isCheckInModule(toDo.todo));
        if (toDo) {
          setCheckInAssignment(toDo);
          await retrieveAssignmentSubmission(userId, toDo.createdAt);
        }
      }
    } catch {
      console.error('Error fetching student ToDos');
    } finally {
      setIsCheckInAssignmentLoaded(true);
      setIsShowSpinner(false);
    }
  };

  const CheckIsCheckingIn = () => {
    // if is daily check in, and hasn't submitted any journal entry for today
    // OR
    // if is check in assignment, and hasn't submitted
    if ((!location.state && !journalEntries) || (location.state && location.state.status !== 'submitted')) {
      setIsCheckingIn(true);
      setIsReviewing(false);
    }
  };

  const CheckIsReviewing = () => {
    // if is daily check in, and has already checked in for today
    // OR
    // if is check in assignment, and has already submitted
    if ((!location.state && journalEntries.length > 0) || (location.state && location.state.status === 'submitted')) {
      setIsReviewing(true);
      setIsCheckingIn(false);
    }
  };

  useEffect(() => {
    if (userProfile && userProfile.type === 'Teacher') {
      setIsLoaded(true);
    } else {
      !isJournalEntriesLoaded && retrieveJournalEntries();
      (!isCheckInAssignmentLoaded || !isAssignmentSubmissionLoaded) && getCheckInAssignment();
      if (isJournalEntriesLoaded && isCheckInAssignmentLoaded && isAssignmentSubmissionLoaded) {
        setIsLoaded(true);
      }
    }
  }, [isJournalEntriesLoaded, isCheckInAssignmentLoaded, isAssignmentSubmissionLoaded]);

  useEffect(() => {
    const hasCheckedIn = getLocalStorage('isCheckInCompleteOrOptedOut');
    if (hasCheckedIn) {
      setIsCheckingIn(false);
      setIsReviewing(true);
    } else {
      setIsCheckingIn(true);
      setIsReviewing(false);
    }
  }, [isLoaded]);

  useEffect(() => {
    if (isLoaded) {
      CheckIsCheckingIn();
      CheckIsReviewing();
    }
  }, [isLoaded]);

  useEffect(() => {
    if (location.state && location.state.status === 'submitted' && assignmentSubmission) {
      setIsViewingSubmission(true);
    }
  }, [isViewingSubmission, location.state, assignmentSubmission]);

  useEffect(() => {
    !toDoHeaderImage && getToDoHeaderImage();
  }, [classroom, userProfile, toDoHeaderImage]);

  return (
    <>
      <SpinnerOverlay isShow={isShowSpinner || !toDoHeaderImage} />
      <StudentHeroSegment home backgroundImage={toDoHeaderImage}>
        <div className={styles.heroText}>
          <h1>Check In</h1>
          <h3>{`Check in with how you're feeling today`}</h3>
        </div>
      </StudentHeroSegment>
      <div className={styles.container}>
        {isCheckingIn && (
          <Fragment key={'emotion wheel'}>
            <div className={styles.titleRow}>
              <h4>How are you feeling today?</h4>
              <button onClick={handlePlayAudio} disabled={isPlayerLoading}>
                <Icon className={styles.playIcon} icon='ooui:volume-up-ltr' />
              </button>
            </div>
            <p>Take a moment to reflect on your feelings, then select an emotion from the wheel below.</p>
            {isSmall ? (
              <CheckInToolMobile handleEmotionClick={handleEmotionClick} selectedEmotion={selectedEmotion} />
            ) : (
              <CheckInTool handleEmotionClick={handleEmotionClick} selectedEmotion={selectedEmotion} />
            )}
            <p>
              {selectedEmotion ? selectedEmotion : <span>&nbsp;&nbsp;</span>}
              {isError && 'Please select an emotion before submitting'}
              {isRateLimited && (
                <span className={styles.errorText}>
                  You can only submit 3 emotions per hour. Please try again later.
                </span>
              )}
            </p>

            <Button thinText darkBlue small onClick={submitJournalEntry}>
              Submit
            </Button>
            <div className={styles.skipEntry} onClick={handleSkipToday}>
              Skip For Today
            </div>
          </Fragment>
        )}
        {isReviewing && (
          <ReviewCheckIn
            studentWork={assignmentSubmission}
            journalEntries={journalEntries}
            handleResetSubmission={handleResetSubmission}
            handleGoHome={handleGoHome}
            isViewDailyCheckIn={!location.state && !isViewingSubmission}
            isViewAssignmentSubmission={isViewingSubmission}
            timeSubmitted={timeSubmitted}
          />
        )}
      </div>
    </>
  );
};

export default CheckIn;
